export interface CompressionOptions {
  quality: number; // 0.1 a 1.0
  maxWidth: number;
  maxHeight: number;
  format: 'jpeg' | 'png' | 'webp';
  maintainAspectRatio: boolean;
}

export interface CompressionResult {
  originalSize: number;
  compressedSize: number;
  compressionRatio: number;
  quality: number;
  format: string;
  dimensions: { width: number; height: number };
  blob: Blob;
}

export class ImageCompressionService {
  private static readonly DEFAULT_OPTIONS: CompressionOptions = {
    quality: 0.8,
    maxWidth: 1920,
    maxHeight: 1080,
    format: 'jpeg',
    maintainAspectRatio: true
  };

  /**
   * Comprime imagem automaticamente baseado no tamanho
   */
  static async autoCompress(file: File): Promise<CompressionResult> {
    const options = this.getAutoCompressionOptions(file);
    return this.compressImage(file, options);
  }

  /**
   * Comprime imagem com opções específicas
   */
  static async compressImage(file: File, options: Partial<CompressionOptions> = {}): Promise<CompressionResult> {
    const finalOptions = { ...this.DEFAULT_OPTIONS, ...options };
    
    try {
      // Criar canvas para processamento
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        throw new Error('Canvas 2D context não disponível');
      }

      // Carregar imagem
      const img = await this.loadImage(file);
      
      // Calcular novas dimensões
      const newDimensions = this.calculateDimensions(
        img.width, 
        img.height, 
        finalOptions.maxWidth, 
        finalOptions.maxHeight, 
        finalOptions.maintainAspectRatio
      );

      // Configurar canvas
      canvas.width = newDimensions.width;
      canvas.height = newDimensions.height;

      // Aplicar filtros de qualidade
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      // Desenhar imagem redimensionada
      ctx.drawImage(img, 0, 0, newDimensions.width, newDimensions.height);

      // Converter para blob com qualidade especificada
      const blob = await this.canvasToBlob(canvas, finalOptions.format, finalOptions.quality);

      // Calcular estatísticas
      const originalSize = file.size;
      const compressedSize = blob.size;
      const compressionRatio = ((originalSize - compressedSize) / originalSize) * 100;

      return {
        originalSize,
        compressedSize,
        compressionRatio,
        quality: finalOptions.quality,
        format: finalOptions.format,
        dimensions: newDimensions,
        blob
      };

    } catch (error) {
      throw new Error(`Erro na compressão: ${error.message}`);
    }
  }

  /**
   * Determina opções de compressão automáticas baseadas no arquivo
   */
  private static getAutoCompressionOptions(file: File): CompressionOptions {
    const sizeMB = file.size / (1024 * 1024);
    
    if (sizeMB > 3) {
      // Arquivo muito grande - compressão agressiva
      return {
        quality: 0.6,
        maxWidth: 1280,
        maxHeight: 720,
        format: 'jpeg',
        maintainAspectRatio: true
      };
    } else if (sizeMB > 1) {
      // Arquivo grande - compressão moderada
      return {
        quality: 0.75,
        maxWidth: 1600,
        maxHeight: 900,
        format: 'jpeg',
        maintainAspectRatio: true
      };
    } else {
      // Arquivo pequeno - compressão mínima
      return {
        quality: 0.85,
        maxWidth: 1920,
        maxHeight: 1080,
        format: 'jpeg',
        maintainAspectRatio: true
      };
    }
  }

  /**
   * Carrega imagem do arquivo
   */
  private static loadImage(file: File): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve(img);
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Falha ao carregar imagem'));
      };

      img.src = url;
    });
  }

  /**
   * Calcula novas dimensões mantendo proporção
   */
  private static calculateDimensions(
    originalWidth: number,
    originalHeight: number,
    maxWidth: number,
    maxHeight: number,
    maintainAspectRatio: boolean
  ): { width: number; height: number } {
    if (maintainAspectRatio) {
      const aspectRatio = originalWidth / originalHeight;
      
      if (originalWidth > maxWidth || originalHeight > maxHeight) {
        if (maxWidth / aspectRatio <= maxHeight) {
          return {
            width: maxWidth,
            height: Math.round(maxWidth / aspectRatio)
          };
        } else {
          return {
            width: Math.round(maxHeight * aspectRatio),
            height: maxHeight
          };
        }
      }
    }

    return {
      width: Math.min(originalWidth, maxWidth),
      height: Math.min(originalHeight, maxHeight)
    };
  }

  /**
   * Converte canvas para blob
   */
  private static canvasToBlob(
    canvas: HTMLCanvasElement, 
    format: string, 
    quality: number
  ): Promise<Blob> {
    return new Promise((resolve, reject) => {
      try {
        if (format === 'webp') {
          // WebP com fallback para JPEG
          canvas.toBlob(
            (blob) => {
              if (blob) {
                resolve(blob);
              } else {
                // Fallback para JPEG se WebP não for suportado
                canvas.toBlob(
                  (jpegBlob) => {
                    if (jpegBlob) {
                      resolve(jpegBlob);
                    } else {
                      reject(new Error('Falha ao criar blob'));
                    }
                  },
                  'image/jpeg',
                  quality
                );
              }
            },
            'image/webp',
            quality
          );
        } else {
          // JPEG ou PNG
          const mimeType = format === 'jpeg' ? 'image/jpeg' : 'image/png';
          canvas.toBlob(
            (blob) => {
              if (blob) {
                resolve(blob);
              } else {
                reject(new Error('Falha ao criar blob'));
              }
            },
            mimeType,
            quality
          );
        }
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Comprime múltiplas imagens em lote
   */
  static async compressBatch(
    files: File[], 
    options: Partial<CompressionOptions> = {}
  ): Promise<CompressionResult[]> {
    const results: CompressionResult[] = [];
    
    for (const file of files) {
      try {
        const result = await this.compressImage(file, options);
        results.push(result);
      } catch (error) {
        console.error(`Erro ao comprimir ${file.name}:`, error);
        // Adicionar resultado de erro
        results.push({
          originalSize: file.size,
          compressedSize: file.size,
          compressionRatio: 0,
          quality: 0,
          format: 'error',
          dimensions: { width: 0, height: 0 },
          blob: file
        });
      }
    }
    
    return results;
  }

  /**
   * Comprime imagem para thumbnail
   */
  static async createThumbnail(
    file: File, 
    size: number = 150
  ): Promise<CompressionResult> {
    return this.compressImage(file, {
      quality: 0.7,
      maxWidth: size,
      maxHeight: size,
      format: 'jpeg',
      maintainAspectRatio: true
    });
  }

  /**
   * Comprime imagem para preview
   */
  static async createPreview(
    file: File, 
    size: number = 300
  ): Promise<CompressionResult> {
    return this.compressImage(file, {
      quality: 0.8,
      maxWidth: size,
      maxHeight: size,
      format: 'jpeg',
      maintainAspectRatio: true
    });
  }

  /**
   * Verifica se compressão é necessária
   */
  static needsCompression(file: File, maxSize: number = 1024 * 1024): boolean {
    return file.size > maxSize;
  }

  /**
   * Obtém estatísticas de compressão
   */
  static getCompressionStats(results: CompressionResult[]): {
    totalFiles: number;
    totalOriginalSize: number;
    totalCompressedSize: number;
    averageCompressionRatio: number;
    spaceSaved: number;
  } {
    const totalFiles = results.length;
    const totalOriginalSize = results.reduce((sum, r) => sum + r.originalSize, 0);
    const totalCompressedSize = results.reduce((sum, r) => sum + r.compressedSize, 0);
    const spaceSaved = totalOriginalSize - totalCompressedSize;
    const averageCompressionRatio = totalFiles > 0 ? 
      results.reduce((sum, r) => sum + r.compressionRatio, 0) / totalFiles : 0;

    return {
      totalFiles,
      totalOriginalSize,
      totalCompressedSize,
      averageCompressionRatio,
      spaceSaved
    };
  }
}
